import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Search, Edit, Eye, ArrowRight } from "lucide-react";

export const StepsSection = () => {
  const steps = [
    {
      number: "1",
      title: "Select Report Type",
      description:
        "Choose from Name Ideation, Trademark Analysis, Domain Research, or Full Research to generate comprehensive brand intelligence.",
      icon: Search,
      features: [
        "Name Ideation - Generate name candidates",
        "Trademark - Generate trademark name conflict analysis",
        "Domain - Match your name candidates with available domains and score them",
        "Full Research - Generate new names, check for trademark name conflicts, and find domain name matches",
      ],
    },
    {
      number: "2",
      title: "Describe Your Brand",
      description:
        "Provide your project description, industry details, and any candidate names you're considering. Our AI uses this context to generate targeted suggestions.",
      icon: Edit,
      features: [
        "Project Description - Tell us about your business concept",
        "Industry Selection - Choose your market sector",
        "Candidate Names - Add any names you're already considering",
        "Target Audience - Specify your ideal customers",
      ],
    },
    {
      number: "3",
      title: "View Results and Iterate",
      description:
        "Review your AI-generated results, try new ideas, and keep improving until you find the perfect fit for your brand.",
      icon: Eye,
      features: [
        "Click Submit to generate your report",
        "Iterate, Refine, and Discover Your Best Name!",
        "Review your results and explore new possibilities",
        "Keep improving until you find the perfect brand name",
      ],
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-white/20 text-white hover:bg-white/30 border-white/20">
            How It Works
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            AI Brand Name Generation and Analysis Steps
          </h2>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto">
            Our streamlined process makes finding the perfect brand name simple
            and efficient. Follow these three steps to discover names that
            resonate with your audience.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.number} className="relative">
                <Card className="h-full bg-white/10 backdrop-blur-sm border-white/20 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    {/* Step Number */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-teal-600 font-bold text-lg">
                        {step.number}
                      </div>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-xl text-teal-100 mb-6 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Features List */}
                    <div className="text-xl space-y-3">
                      {step.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-start space-x-3"
                        >
                          <div className="w-2 h-2 bg-white rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-lg text-teal-100">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Arrow connector for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border border-white/20">
                      <ArrowRight className="w-4 h-4 text-teal-600" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
